// Seamless clouds for the last day and month of the timeline. The API keeps
// small scrubbing frames for the last ~26 hours and for each midnight of the
// last month, the moments the month scale steps through (/clouds_recent
// lists them, /clouds_frame?t=YYYY-MM-DDTHH serves one: raw infrared
// brightness as a 1024x512 greyscale JPEG). They are all fetched up front,
// so while the timeline moves the clouds come from memory: blended from the
// two hours either side of it, or that day's midnight frame.

const HOUR_MS = 3_600_000;
export const FRAME_WIDTH = 1024;
export const FRAME_HEIGHT = 512;

// Brightness → grey and alpha, as the API maps it for /clouds (the same
// breakpoints as _BRIGHTNESS/_GREY/_ALPHA in bluedot/clouds.py).
const BRIGHTNESS = [0, 66, 69, 76, 88, 98, 110, 128, 167, 183, 201, 255];
const GREY = [128, 130, 136, 169, 194, 210, 223, 237, 245, 247, 250, 252];
const ALPHA = [0, 3, 7, 82, 136, 168, 194, 221, 237, 241, 245, 248];

function lut(ys) {
  const out = new Uint8Array(256);
  for (let v = 0, i = 0; v < 256; v++) {
    while (BRIGHTNESS[i + 1] < v) i++;
    const t = (v - BRIGHTNESS[i]) / (BRIGHTNESS[i + 1] - BRIGHTNESS[i]);
    out[v] = Math.floor(ys[i] + t * (ys[i + 1] - ys[i]));
  }
  return out;
}
export const GREY_LUT = lut(GREY);
export const ALPHA_LUT = lut(ALPHA);

export const hourToMs = (hour) => Date.parse(`${hour}:00:00Z`);
export const msToHour = (ms) => new Date(ms).toISOString().slice(0, 13);

// The two frames either side of `timeMs` and how far it is from the first to
// the second, from the frame hours on hand (ms, ascending). Past the newest
// frame (NOAA publishes each hour ~35 minutes late) the newest stands in;
// before the oldest there's nothing to show (null).
export function frameBlend(hours, timeMs) {
  if (!hours.length || timeMs < hours[0]) return null;
  const last = hours[hours.length - 1];
  if (timeMs >= last) return timeMs - last <= 3 * HOUR_MS ? { a: last, b: last, t: 0 } : null;
  let i = 0;
  while (hours[i + 1] <= timeMs) i++;
  const a = hours[i];
  const b = hours[i + 1];
  return { a, b, t: (timeMs - a) / (b - a) };
}

// Blends two brightness frames and maps the result to luminance+alpha
// RGBA pixels (grey in rgb, alpha — what the cloud shader reads; a
// luminance+alpha texture isn't valid in WebGL2, so these are four bytes a pixel).
export function paintBlend(out, a, b, t) {
  const w = Math.round(t * 256);
  const v = 256 - w;
  for (let i = 0, j = 0; i < a.length; i++, j += 4) {
    const px = (a[i] * v + b[i] * w) >> 8;
    out[j] = out[j + 1] = out[j + 2] = GREY_LUT[px];
    out[j + 3] = ALPHA_LUT[px];
  }
  return out;
}

// A single brightness map as luminance+alpha pairs.
export function paintMap(out, brightness) {
  for (let i = 0, j = 0; i < brightness.length; i++, j += 4) {
    out[j] = out[j + 1] = out[j + 2] = GREY_LUT[brightness[i]];
    out[j + 3] = ALPHA_LUT[brightness[i]];
  }
  return out;
}

// The frame set. list() resolves to the API's /clouds_recent listing
// ({ hours, days, live }); loadFrame(hour) to that frame's brightness
// (Uint8Array). refresh() adds new hours and drops ones the API no longer
// lists; onChange runs whenever a frame arrives or leaves, onLive(hour) when
// the Live hour (whose full map is ready) changes.
export function createRecentClouds({ list, loadFrame, onChange = () => {}, onLive = () => {} }) {
  const frames = new Map(); // hour ms -> Uint8Array
  const loading = new Set();
  let hours = []; // loaded hourly frames (ms, ascending)
  let hourSet = new Set();
  let daySet = new Set();
  let live = null;
  const index = () => {
    hours = [...frames.keys()].filter((h) => hourSet.has(h)).sort((x, y) => x - y);
  };

  const refresh = async () => {
    const listing = await list();
    if (listing.live && listing.live !== live) {
      live = listing.live;
      onLive(live);
    }
    hourSet = new Set(listing.hours.map(hourToMs));
    daySet = new Set((listing.days || []).map(hourToMs));
    const listed = [...new Set([...hourSet, ...daySet])];
    let changed = false;
    for (const h of [...frames.keys()]) {
      if (!hourSet.has(h) && !daySet.has(h)) {
        frames.delete(h);
        changed = true;
      }
    }
    if (changed) {
      index();
      onChange();
    }
    // Newest first: the end of the timeline is where scrubbing starts.
    const missing = listed.filter((h) => !frames.has(h) && !loading.has(h)).sort((x, y) => y - x);
    await Promise.all(missing.map(async (h) => {
      loading.add(h);
      try {
        frames.set(h, await loadFrame(msToHour(h)));
        index();
        onChange();
      } catch (_) {
        // Retried on the next refresh.
      } finally {
        loading.delete(h);
      }
    }));
  };

  // Paints `timeMs`'s clouds (blended hours) into `out`; false when no
  // frame covers it.
  const paint = (out, timeMs) => {
    const blend = frameBlend(hours, timeMs);
    if (!blend) return false;
    paintBlend(out, frames.get(blend.a), frames.get(blend.b), blend.t);
    return true;
  };

  // A month-scale step: whether `timeMs` is a midnight whose frame is here.
  const hasDay = (timeMs) => daySet.has(timeMs) && frames.has(timeMs);

  // Paints that midnight's frame into `out`; false when it isn't here.
  const paintDay = (out, timeMs) => {
    if (!hasDay(timeMs)) return false;
    paintMap(out, frames.get(timeMs));
    return true;
  };

  return { refresh, paint, hasDay, paintDay, hours: () => hours };
}
